import { PromocodesStatus } from '@spike/promocodes-action';
import { createSelector } from 'reselect';
import {
    getOnlineBookingTotalAmount,
    getOnlineBookingTotalDeposit
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { RootState } from 'store';
import {
    estimatePromocodeDiscount,
    resolveBookingDeposit,
    roundMoney,
    toMoney
} from 'utils/promoCodeUtils';

export const getPromocodesState = (state: RootState) => state.promocodes;

export const getValidatedPromocode = createSelector(
    getPromocodesState,
    promocodes => promocodes.validatedPromocode
);

export const getBookingPromocode = createSelector(
    getPromocodesState,
    promocodes => promocodes.bookingPromocode
);

export const getPromocodeError = createSelector(getPromocodesState, promocodes => promocodes.error);

export const getPromocodeLoading = createSelector(getPromocodesState, promocodes => {
    return (
        promocodes.loading &&
        [
            PromocodesStatus.Validating,
            PromocodesStatus.Applying,
            PromocodesStatus.Removing
        ].includes(promocodes.status)
    );
});

export const getAppliedPromocodeName = createSelector(getPromocodesState, promocodes => {
    if (promocodes.bookingPromocode?.promocode?.name) {
        return promocodes.bookingPromocode.promocode.name;
    }

    const keepValidated = [PromocodesStatus.ValidateSuccess, PromocodesStatus.Applying].includes(
        promocodes.status
    );

    return keepValidated ? promocodes.validatedPromocode?.name : undefined;
});

export interface ResumePricing {
    subtotal: number;
    discount: number | null;
    deposit: number;
    amountDue: number;
    fromInvoice: boolean;
}

export const getResumePricing = createSelector(
    [
        getOnlineBookingTotalAmount,
        getOnlineBookingTotalDeposit,
        getPromocodesState,
        (state: RootState) => state.invoices.depositInvoice
    ],
    (clientSubtotal, clientDeposit, promocodes, depositInvoice): ResumePricing => {
        const applied = promocodes.bookingPromocode;

        if (applied) {
            const discount = applied.promocode ? toMoney(applied.discountAmount) : null;
            const subtotal = toMoney(applied.subtotal) || clientSubtotal;
            const deposit = resolveBookingDeposit(
                applied.deposit,
                depositInvoice?.deposits?.unpaidTotal,
                depositInvoice?.deposits?.total,
                clientDeposit
            );
            const totalAfterDiscount =
                toMoney(applied.total) || roundMoney(subtotal - (discount ?? 0));
            const amountDue =
                toMoney(applied.deposit) > 0
                    ? toMoney(applied.amountDue)
                    : Math.max(0, roundMoney(totalAfterDiscount - deposit));

            return {
                subtotal,
                discount,
                deposit,
                amountDue,
                fromInvoice: true
            };
        }

        const keepEstimate = [PromocodesStatus.ValidateSuccess, PromocodesStatus.Applying].includes(
            promocodes.status
        );

        const discount =
            promocodes.validatedPromocode && keepEstimate
                ? estimatePromocodeDiscount(
                      clientSubtotal,
                      promocodes.validatedPromocode.discountType,
                      promocodes.validatedPromocode.discountValue
                  )
                : null;

        return {
            subtotal: clientSubtotal,
            discount,
            deposit: clientDeposit,
            amountDue: Math.max(0, roundMoney(clientSubtotal - clientDeposit - (discount ?? 0))),
            fromInvoice: false
        };
    }
);
